import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, subDays, parseISO } from 'date-fns';
import {
  BookOpen, Plus, Calendar as CalendarLucide, Lightbulb, Pencil,
  Sparkles, Flame, FileText, TrendingUp, Flag as FlagIcon, X, Loader2,
} from 'lucide-react';
import { reflectionsApi, driversApi } from '../api/services';
import DictationTextField from '../components/DictationTextField';
import GuidedReflectionDialog from '../components/GuidedReflectionDialog';
import CoachingInsightsCard from '../components/CoachingInsightsCard';
import {
  detectPatterns,
  describePattern,
  findDriverMentions,
  type DriverLike,
  type PatternTheme,
} from '../utils/reflectionPatterns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// ReflectionsPage — daily journal surface. Top of the page is the date picker
// and quick-add affordances; below that is a streak summary, a weekly digest
// pill, a recurring-themes strip (only when patterns actually exist), then
// the day's reflections and the previous week.
//
// One MUI hold-out: the DictationTextField in the Add-Reflection dialog. It
// wraps MUI TextField + speech-to-text, and rewriting it to shadcn is a
// separate refactor (the dictation icon + animation lives inside the MUI
// InputAdornment). The dialog around it is shadcn.
// ---------------------------------------------------------------------------

interface Reflection {
  id: string;
  date: string;
  trigger: string;
  content: string;
  insights?: string;
  adjustmentsMade?: string;
  createdAt: string;
}

interface TriggerTone {
  label: string;
  bar: string;
  chip: string;
}

const TRIGGER_TONES: Record<string, TriggerTone> = {
  CloseDay: {
    label: 'Close Day',
    bar: 'border-l-primary',
    chip: 'bg-primary/15 text-primary',
  },
  MiddayReset: {
    label: 'Midday Reset',
    bar: 'border-l-amber-500',
    chip: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  },
  WeeklyReset: {
    label: 'Weekly Reset',
    bar: 'border-l-violet-500',
    chip: 'bg-violet-500/15 text-violet-700 dark:text-violet-400',
  },
  Manual: {
    label: 'Manual',
    bar: 'border-l-emerald-500',
    chip: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  },
  PlanTomorrow: {
    label: 'Plan Tomorrow',
    bar: 'border-l-violet-400',
    chip: 'bg-violet-400/15 text-violet-700 dark:text-violet-300',
  },
};

const DEFAULT_TONE: TriggerTone = {
  label: '',
  bar: 'border-l-border',
  chip: 'bg-muted text-muted-foreground',
};

function getTriggerTone(trigger: string): TriggerTone {
  return TRIGGER_TONES[trigger] ?? { ...DEFAULT_TONE, label: trigger };
}

export default function ReflectionsPage() {
  const navigate = useNavigate();

  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [todayReflections, setTodayReflections] = useState<Reflection[]>([]);
  const [pastReflections, setPastReflections] = useState<Reflection[]>([]);
  const [allRecentReflections, setAllRecentReflections] = useState<Reflection[]>([]); // 30 days for streak + patterns
  const [activeDrivers, setActiveDrivers] = useState<DriverLike[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add Reflection dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newReflection, setNewReflection] = useState({
    trigger: 'Manual',
    content: '',
    insights: '',
    adjustmentsMade: '',
  });
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [guidedOpen, setGuidedOpen] = useState(false);

  const loadReflections = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Load selected date reflections
      let todayData: Reflection[] = [];
      try {
        const result = await reflectionsApi.getByDate(selectedDate);
        todayData = Array.isArray(result) ? result : result ? [result] : [];
      } catch (err: any) {
        if (err.response?.status !== 404) throw err;
      }
      setTodayReflections(todayData);

      // Load past 30 days (7 for display, 30 for streak tracking)
      const pastResults: Reflection[] = [];
      const allResults: Reflection[] = [];
      for (let i = 1; i <= 30; i++) {
        const pastDate = format(subDays(parseISO(selectedDate), i), 'yyyy-MM-dd');
        try {
          const result = await reflectionsApi.getByDate(pastDate);
          const items = Array.isArray(result) ? result : result ? [result] : [];
          allResults.push(...items);
          if (i <= 7) pastResults.push(...items);
        } catch {
          // 404 is expected for days without reflections
        }
      }
      setPastReflections(pastResults);
      setAllRecentReflections([...todayData, ...allResults]);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load reflections');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => { loadReflections(); }, [loadReflections]);

  // Driver fetch is done once — we only need titles for the linkage scan,
  // and the list is small enough that skipping a re-fetch on every date change
  // is a worthwhile saving.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const drivers = await driversApi.getActive();
        if (cancelled) return;
        setActiveDrivers((drivers || []).map((d: any) => ({ id: d.id, title: d.title })));
      } catch {
        // Driver fetch is non-critical for this page — surface silently.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleCreateReflection = async () => {
    if (!newReflection.content.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await reflectionsApi.create({
        date: selectedDate,
        trigger: newReflection.trigger,
        content: newReflection.content,
        insights: newReflection.insights || undefined,
        adjustmentsMade: newReflection.adjustmentsMade || undefined,
      });
      setDialogOpen(false);
      setNewReflection({ trigger: 'Manual', content: '', insights: '', adjustmentsMade: '' });
      setSaveSuccess('Reflection saved successfully.');
      loadReflections();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save reflection');
    } finally {
      setSaving(false);
    }
  };

  // Streak tracking: count consecutive days with reflections (current + longest)
  const { currentStreak, longestStreak } = useMemo(() => {
    if (allRecentReflections.length === 0) return { currentStreak: 0, longestStreak: 0 };
    const dates = Array.from(new Set(allRecentReflections.map(r => r.date))).sort().reverse();

    // Current streak: consecutive days ending at today
    let current = 0;
    const today = new Date().toISOString().split('T')[0];
    let checkDate = today;
    for (const d of dates) {
      if (d === checkDate) {
        current++;
        const prev = new Date(checkDate);
        prev.setDate(prev.getDate() - 1);
        checkDate = prev.toISOString().split('T')[0];
      } else if (d < checkDate) {
        break;
      }
    }

    // Longest streak: find the longest run of consecutive days in the data
    const sortedAsc = [...dates].sort();
    let longest = 0;
    let run = 1;
    for (let i = 1; i < sortedAsc.length; i++) {
      const prev = new Date(sortedAsc[i - 1]);
      prev.setDate(prev.getDate() + 1);
      if (prev.toISOString().split('T')[0] === sortedAsc[i]) {
        run++;
      } else {
        longest = Math.max(longest, run);
        run = 1;
      }
    }
    longest = Math.max(longest, run);

    return { currentStreak: current, longestStreak: longest };
  }, [allRecentReflections]);

  // Weekly reflection digest — count only.
  const weekDigest = useMemo(() => {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const weekReflections = allRecentReflections.filter(r => {
      const d = new Date(r.date);
      return d >= weekAgo;
    });
    if (weekReflections.length === 0) return null;
    return { count: weekReflections.length };
  }, [allRecentReflections]);

  // Pattern surfacing: scan the trailing 30 days for recurring themes. Only
  // themes that appear on two or more distinct days surface — a single bad
  // day shouldn't become "a pattern the exec is battling".
  const patterns: PatternTheme[] = useMemo(
    () => detectPatterns(allRecentReflections),
    [allRecentReflections],
  );

  const isToday = selectedDate === format(new Date(), 'yyyy-MM-dd');

  return (
    <div className="mx-auto max-w-[1200px] p-4 md:p-6">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BookOpen className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Reflections</h1>
            <p className="text-sm text-muted-foreground">Daily journal and insights</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-[160px]"
          />
          <Button onClick={() => setGuidedOpen(true)} className="gap-1.5">
            <Sparkles className="h-4 w-4" />
            Guided Reflection
          </Button>
          <Button variant="outline" onClick={() => setDialogOpen(true)} className="gap-1.5">
            <Plus className="h-4 w-4" />
            Quick Add
          </Button>
        </div>
      </div>

      {loading && (
        <div className="mb-4 h-1 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-full w-1/3 animate-pulse bg-primary" />
        </div>
      )}
      {error && (
        <Alert variant="error" className="mb-4">
          <div className="flex items-start justify-between gap-3">
            <AlertDescription className="flex-1">{error}</AlertDescription>
            <button
              type="button"
              onClick={() => setError(null)}
              aria-label="Dismiss"
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </Alert>
      )}
      {saveSuccess && (
        <Alert variant="success" className="mb-4">
          <div className="flex items-start justify-between gap-3">
            <AlertDescription className="flex-1">{saveSuccess}</AlertDescription>
            <button
              type="button"
              onClick={() => setSaveSuccess(null)}
              aria-label="Dismiss"
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </Alert>
      )}

      {/* Coaching insights — surfaces the AI's nudges (stalled driver, red
          project, carry-forward tail) at the moment the exec is looking back
          on the day. Self-hides when there is nothing to say. */}
      <CoachingInsightsCard title="Coaching for today" />

      {/* Streak + weekly digest */}
      {!loading && (
        <div className="mb-6 flex flex-col gap-3 md:flex-row">
          <Card
            className={cn(
              'flex-1 border-l-[3px]',
              currentStreak > 0 ? 'border-l-violet-500' : 'border-l-border',
            )}
          >
            <CardContent className="py-3">
              <div className="flex items-center gap-2">
                <Flame
                  className={cn(
                    'h-5 w-5',
                    currentStreak > 0 ? 'text-violet-500' : 'text-muted-foreground',
                  )}
                />
                <p
                  className={cn(
                    'text-sm font-semibold',
                    currentStreak > 0 ? 'text-violet-600 dark:text-violet-300' : 'text-muted-foreground',
                  )}
                >
                  {currentStreak > 0 ? `${currentStreak} day streak` : 'Start your streak today'}
                </p>
                {longestStreak > 0 && (
                  <span className="inline-flex items-center rounded-full bg-violet-500/15 px-2 py-0.5 text-[11px] font-semibold text-violet-600 dark:text-violet-300">
                    Best: {longestStreak}d
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
          {weekDigest && (
            <Card className="flex-1 border-l-[3px] border-l-primary">
              <CardContent className="py-3">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  <p className="text-sm">
                    <strong>This week:</strong> {weekDigest.count} reflection{weekDigest.count !== 1 ? 's' : ''}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Recurring themes strip — only renders when 2+ distinct days in the
          last 30 show the same theme. */}
      {!loading && patterns.length > 0 && (
        <Card className="mb-6 border-l-[3px] border-l-amber-500">
          <CardContent className="py-3">
            <div className="mb-2 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">Recurring themes</p>
              <p className="text-xs text-muted-foreground">Last 30 days</p>
            </div>
            <div className="flex flex-col gap-3">
              {patterns.slice(0, 5).map((p) => (
                <div key={p.key}>
                  <p className="text-sm">{describePattern(p)}</p>
                  {/* Behavioural nudge — pair the recurring theme with a
                      concrete next step. */}
                  <div className="mt-0.5 ml-0.5 flex items-center gap-2">
                    <p className="flex-1 text-xs text-muted-foreground">{p.suggestion.text}</p>
                    <Button
                      size="sm"
                      variant="link"
                      onClick={() => navigate(p.suggestion.href)}
                      className="h-auto p-0 text-[11px]"
                    >
                      {p.suggestion.cta} →
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Today's reflections heading */}
      <h2 className="mb-3 text-lg font-semibold">
        {isToday
          ? "Today's Reflections"
          : `Reflections for ${format(parseISO(selectedDate), 'dd MMM yyyy')}`}
      </h2>

      {!loading && todayReflections.length === 0 && (
        <Card className="mb-6">
          <CardContent className="py-10 text-center">
            <BookOpen className="mx-auto mb-3 h-12 w-12 text-muted-foreground/50" />
            <p className="text-base font-semibold text-muted-foreground">No reflections yet</p>
            <p className="mb-3 mt-1 text-sm text-muted-foreground">
              Reflections are captured when you close your day, or you can add one manually.
            </p>
            <Button variant="outline" onClick={() => setDialogOpen(true)} className="gap-1.5">
              <Plus className="h-4 w-4" />
              Add Reflection
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="mb-8 flex flex-col gap-3">
        {todayReflections.map((reflection) => (
          <ReflectionCard
            key={reflection.id}
            reflection={reflection}
            drivers={activeDrivers}
          />
        ))}
      </div>

      {/* Past reflections */}
      {pastReflections.length > 0 && (
        <>
          <h2 className="mb-3 mt-2 text-lg font-semibold">Past 7 Days</h2>
          <div className="flex flex-col gap-3">
            {pastReflections.map((reflection) => (
              <ReflectionCard
                key={reflection.id}
                reflection={reflection}
                drivers={activeDrivers}
              />
            ))}
          </div>
        </>
      )}

      {/* Add Reflection dialog — three-prompt shape */}
      <Dialog open={dialogOpen} onOpenChange={(o) => !o && setDialogOpen(false)}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add reflection</DialogTitle>
            <p className="text-sm text-muted-foreground">Three prompts. Keep them short.</p>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="refl-trigger">Trigger</Label>
              <Select
                value={newReflection.trigger}
                onValueChange={(v) => setNewReflection(prev => ({ ...prev, trigger: v }))}
              >
                <SelectTrigger id="refl-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Manual">Manual</SelectItem>
                  <SelectItem value="CloseDay">Close Day</SelectItem>
                  <SelectItem value="MiddayReset">Midday Reset</SelectItem>
                  <SelectItem value="WeeklyReset">Weekly Reset</SelectItem>
                  <SelectItem value="PlanTomorrow">Plan Tomorrow</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* DictationTextField is the one MUI hold-out — it owns the
                speech-to-text adornment + animation. Visually distinct from
                the surrounding shadcn inputs; rewriting it is a separate
                refactor tracked in BACKLOG. */}
            <DictationTextField
              label="What worked"
              multiline
              rows={3}
              fullWidth
              value={newReflection.content}
              onChange={(e) => setNewReflection(prev => ({ ...prev, content: (e.target as HTMLTextAreaElement).value }))}
              onTranscriptChange={(text) => setNewReflection(prev => ({ ...prev, content: text }))}
              placeholder="What went well today? Where did you make progress?"
            />
            <DictationTextField
              label="What didn't"
              multiline
              rows={3}
              fullWidth
              value={newReflection.insights}
              onChange={(e) => setNewReflection(prev => ({ ...prev, insights: (e.target as HTMLTextAreaElement).value }))}
              onTranscriptChange={(text) => setNewReflection(prev => ({ ...prev, insights: text }))}
              placeholder="What slipped, got stuck, or drained you?"
            />
            <DictationTextField
              label="What changes tomorrow"
              multiline
              rows={2}
              fullWidth
              value={newReflection.adjustmentsMade}
              onChange={(e) => setNewReflection(prev => ({ ...prev, adjustmentsMade: (e.target as HTMLTextAreaElement).value }))}
              onTranscriptChange={(text) => setNewReflection(prev => ({ ...prev, adjustmentsMade: text }))}
              placeholder="One thing you will do differently."
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreateReflection}
              disabled={saving || !newReflection.content.trim()}
              className="gap-1.5"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? 'Saving...' : 'Save Reflection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <GuidedReflectionDialog
        open={guidedOpen}
        onClose={() => setGuidedOpen(false)}
        onSaved={() => { setGuidedOpen(false); loadReflections(); setSaveSuccess('Guided reflection saved successfully.'); }}
        date={selectedDate}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReflectionCard — one reflection. Left edge bar + chip tone are driven by
// the trigger (Close Day / Midday Reset / Weekly Reset / Manual / Plan
// Tomorrow). Each populated section (What worked / didn't / changes
// tomorrow) gets its own tinted panel so the three-prompt shape stays
// scannable.
// ---------------------------------------------------------------------------
function ReflectionCard({
  reflection,
  drivers,
}: {
  reflection: Reflection;
  drivers: DriverLike[];
}) {
  const tone = getTriggerTone(reflection.trigger);

  let displayDate: string;
  try {
    displayDate = format(parseISO(reflection.date), 'EEEE, dd MMM yyyy');
  } catch {
    displayDate = reflection.date;
  }

  const driverMentions = useMemo(
    () => findDriverMentions(reflection, drivers),
    [reflection, drivers],
  );

  return (
    <Card className={cn('border-l-[3px]', tone.bar)}>
      <CardContent>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <CalendarLucide className="h-4 w-4 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">{displayDate}</p>
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold',
                tone.chip,
              )}
            >
              {tone.label}
            </span>
            {driverMentions.map((m) => (
              <span
                key={m.driverId}
                className="inline-flex items-center gap-1 rounded-full bg-violet-500/15 px-2 py-0.5 text-[11px] font-medium text-violet-700 dark:text-violet-300"
              >
                <FlagIcon className="h-3 w-3" />
                {m.driverTitle}
              </span>
            ))}
          </div>
          {reflection.createdAt && (
            <p className="text-xs text-muted-foreground">
              {format(parseISO(reflection.createdAt), 'HH:mm')}
            </p>
          )}
        </div>

        {reflection.content && (
          <div className={reflection.insights || reflection.adjustmentsMade ? 'mb-3' : ''}>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.05em] text-emerald-600 dark:text-emerald-400">
              What worked
            </p>
            <p className="whitespace-pre-wrap text-sm">{reflection.content}</p>
          </div>
        )}

        {reflection.insights && (
          <div className="mt-3 rounded-xl border border-amber-500/20 bg-amber-500/[0.06] p-3">
            <div className="mb-1 flex items-center gap-1.5">
              <Lightbulb className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-amber-700 dark:text-amber-400">
                What didn&apos;t
              </p>
            </div>
            <p className="whitespace-pre-wrap text-sm">{reflection.insights}</p>
          </div>
        )}

        {reflection.adjustmentsMade && (
          <div className="mt-3 rounded-xl border border-primary/20 bg-primary/[0.06] p-3">
            <div className="mb-1 flex items-center gap-1.5">
              <Pencil className="h-4 w-4 text-primary" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-primary">
                What changes tomorrow
              </p>
            </div>
            <p className="whitespace-pre-wrap text-sm">{reflection.adjustmentsMade}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
